import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, ArrowLeft, RefreshCw, CheckCircle2 } from "lucide-react";

import { API_URL } from "@/utils/api";

export default function VerifyEmailPending() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get("email") || "";
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || !email) return;
    
    setResending(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to resend email");
      }
      
      toast.success("Verification email sent! Check your inbox.");
      setCooldown(60); // 60 second cooldown
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex font-['Inter']">
      {/* Left Side - Content */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-24 bg-white">
        <div className="max-w-md w-full mx-auto">
          <Link to="/login" className="inline-flex items-center text-[#121212]/60 hover:text-[#121212] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to login
          </Link>
          
          <div className="mb-8 text-center">
            <div className="w-20 h-20 bg-[#F7FFE5] rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-dashed border-[#BFFF00]">
              <Mail className="w-10 h-10 text-[#121212]" />
            </div>
            <h1 className="text-3xl font-bold text-[#121212] mb-2 font-['Manrope']">Check your email</h1>
            <p className="text-[#121212]/60">
              We've sent a verification link to
            </p>
            {email && (
              <p className="text-[#121212] font-semibold mt-1">{email}</p>
            )}
          </div>

          <div className="bg-[#F7FFE5] rounded-2xl p-6 mb-8 border-2 border-[#BFFF00]/30">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-6 h-6 text-[#121212] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-[#121212] mb-1">Next steps:</h3>
                <ol className="text-sm text-[#121212]/70 space-y-2">
                  <li>1. Open the email from FinAI by Revurge</li>
                  <li>2. Click the "Verify Email Address" button</li>
                  <li>3. You'll be automatically signed in</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-[#121212]/60 mb-4">Didn't receive the email?</p>
            <Button
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              variant="outline"
              className="rounded-full border-2 border-[#121212] hover:bg-[#121212] hover:text-white px-6"
              data-testid="resend-verification-btn"
            >
              {resending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : cooldown > 0 ? (
                `Resend in ${cooldown}s`
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend verification email
                </>
              )}
            </Button>
            
            <p className="text-sm text-[#121212]/50 mt-6">
              Check your spam folder if you don't see the email
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #F7FFE5 0%, #EDFFC2 100%)' }}>
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'linear-gradient(#BFFF00 1px, transparent 1px), linear-gradient(90deg, #BFFF00 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        
        <div className="relative max-w-lg text-center">
          <div className="w-24 h-24 bg-[#BFFF00] rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-[#BFFF00]/30">
            <span className="text-5xl font-extrabold text-[#121212] font-['Manrope']">F</span>
          </div>
          <h2 className="text-4xl font-bold text-[#121212] mb-4 font-['Manrope']">Almost there!</h2>
          <p className="text-[#121212]/60 text-lg leading-relaxed">
            Verify your email to unlock full access to professional bookkeeping and accounting services.
          </p>
        </div>
      </div>
    </div>
  );
}
